// import { useDiscountVoucherValidation } from 'hooks/useSalesEntryAPI';
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useDiscountVoucherValidation } from '../../services';
import { ConsumerOfferDetail, useSalesEntry } from 'contexts/SalesEntryContext';
import { SalesEntryData } from 'contexts/SalesEntryContext';
import notistack from 'modules/core/notistack';

export interface CouponData {
  id: string;
  code: string;
  discount: number;
  couponPrice: number;
  couponTypeId: number;
  couponCode: string | null;
  withGst: boolean | null;
  isConsumerOfferApplicable: boolean | null;
}

interface CouponModalProps {
  show: boolean;
  onHide: () => void;
  currentCoupon: CouponData | null;
  onCouponSubmit: (coupon: CouponData | null) => void;
  consumerOfferDetail?: ConsumerOfferDetail[];
}

interface FormErrors {
  voucherCode?: string;
  api?: string;
}

export const CouponModal: React.FC<CouponModalProps> = ({
  show,
  onHide,
  currentCoupon,
  onCouponSubmit,
  consumerOfferDetail
}) => {
  const { state, actions } = useSalesEntry();
  const [voucherCode, setVoucherCode] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const { validateDiscountVoucher, loading, error } = useDiscountVoucherValidation();

  useEffect(() => {
    if (show && currentCoupon) {
      setVoucherCode(currentCoupon.code);
    } else if (show && !currentCoupon) {
      setVoucherCode('');
    }
    setErrors({});
  }, [show, currentCoupon]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!voucherCode.trim()) {
      newErrors.voucherCode = 'Discount voucher code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to add Gift Voucher payment
  const addGiftVoucherPayment = (couponAmount: number, voucherCode: string) => {
    // Check if Gift Voucher payment with same voucher code already exists
    const existingGiftVoucher = state.payment?.find(
      payment => payment.method === 'Gift Voucher' && payment.referenceNumber === voucherCode
    );

    if (!existingGiftVoucher) {
      // Add new Gift Voucher payment
      actions.addPayment({
        method: 'Gift Voucher',
        amount: couponAmount,
        referenceNumber: voucherCode,
        discountAmount: 0,
      });
    }
  };

  // Helper function to remove Gift Voucher payment
  const removeGiftVoucherPayment = (voucherCode: string) => {
    if (state.payment && state.payment.length > 0) {
      // Remove the specific Gift Voucher payment
      const filteredPayments = state.payment.filter(
        payment => !(payment.method === 'Gift Voucher' && payment.referenceNumber === voucherCode)
      );

      // Clear all payments and re-add the filtered ones
      actions.clearPayments();
      filteredPayments.forEach(payment => {
        actions.addPayment(payment);
      });
    }
  };

  // Helper function to check if current coupon has associated Gift Voucher payment
  const hasAssociatedGiftVoucher = () => {
    if (!currentCoupon || !state.payment) return false;
    return state.payment.some(
      payment => payment.method === 'Gift Voucher' && payment.referenceNumber === currentCoupon.code
    );
  };

  // Helper function to sync coupon removal when Gift Voucher payment is deleted
  const syncCouponRemovalFromPayment = () => {
    // This function can be called from parent component when Gift Voucher payment is deleted
    if (currentCoupon && !hasAssociatedGiftVoucher()) {
      onCouponSubmit(null);
      toast.info(`Coupon "${currentCoupon.code}" removed because its Gift Voucher payment was deleted.`);
    }
  };

  const handleSubmit = async () => {
    // Step 1: Validate local form
    if (!validateForm()) {
      return;
    }

    if (state.discount != null && state.discount > 0) {
      notistack.warning('The discount has been removed as a new coupon code was applied.Please reapply the discount to continue.')
      actions.setDiscount(0);

    }

    // Step 2: Clear previous API errors
    setErrors(prev => ({ ...prev, api: undefined }));

    try {
      // Step 3: Validate the voucher using API (with metalKtcIds)
      if (!state.customer?.id) {
        notistack.warning("Select a customer before proceeding");
        return
      }
      const voucherDetails = await validateDiscountVoucher(voucherCode.trim(), [...(state.items.map(item => item.ktId))], state.customer?.id);

      // Step 4: If valid, prepare and submit coupon data
      if (voucherDetails && voucherDetails.isActive) {
        const totalAmt = state.items.reduce((total, item) => total + (item.mrpPrice || 0), 0);

        if (voucherDetails.minInvoiceAmt > totalAmt) {
          toast.warning(`Invoice amount (${totalAmt}) should be greater than the voucher amount (${voucherDetails.minInvoiceAmt}).`);
          return;
        }

        const offers = state.consumerOfferDetail || [];
        const realOffers = offers.filter(offer =>
        ((offer.productId && !offer.productId.startsWith('test_') &&
          state?.items.some(item => item.id === offer.productId))
          || !offer.productId
        ));

        const selectedOffers = realOffers.filter(offer => offer.isSelected) ?? [];

        let discountValue = 0;
        if (voucherDetails.voucherValueIsPercentage) {
          discountValue = (totalAmt * voucherDetails.voucherValue) / 100; // percentage
        } else {
          discountValue = voucherDetails.voucherValue; // fixed amount
        }

        if (voucherDetails.isConsumerOfferApplicable || selectedOffers.length == 0) {
          const couponData: CouponData = {
            id: voucherDetails.id.toString(),
            code: voucherCode.trim(),
            discount: discountValue,
            couponPrice: discountValue,
            couponTypeId: voucherDetails.id,
            couponCode: voucherCode.trim(),
            withGst: voucherDetails.withGst,
            isConsumerOfferApplicable: voucherDetails.isConsumerOfferApplicable,
          };

          if (voucherDetails?.withGst) {
            addGiftVoucherPayment(discountValue || 0, voucherCode.trim());
          } else {
            if (state.payment && state.payment.length > 0) {
              // Remove the specific Gift Voucher payment
              const filteredPayments = state.payment.filter(
                payment => !(payment.method === 'Gift Voucher')
              );

              // Clear all payments and re-add the filtered ones
              actions.clearPayments();
              filteredPayments.forEach(payment => {
                actions.addPayment(payment);
              });
            }
          }


          onCouponSubmit(couponData);
          toast.success(`Coupon code "${voucherCode.trim()}" applied successfully! Discount:${voucherDetails.voucherValueIsPercentage ? '%' : "₹"}${voucherDetails.voucherValue} added as Gift Voucher payment`);
        }
        else if (selectedOffers.length > 0) {
          toast.warning("This voucher code can't be clubbed with a consumer offer.");
        }
      } else {
        setErrors(prev => ({ ...prev, api: 'Invalid voucher code or voucher is not active' }));
      }

      handleClose();
    } catch (err: any) {
      setErrors(prev => ({
        ...prev,
        api: err?.response?.data?.message || 'Failed to validate voucher code'
      }));
    }
  };

  const handleRemove = () => {
    if (currentCoupon) {
      removeGiftVoucherPayment(currentCoupon.code);
    }

    onCouponSubmit(null);
    handleClose();
  };

  const handleClose = () => {
    setVoucherCode('');
    setErrors({});
    onHide();
  };

  const clearError = () => {
    if (errors.voucherCode || errors.api) {
      setErrors(prev => ({ ...prev, voucherCode: '', api: '' }));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">
          <i className="bx bx-gift me-2"></i>
          Discount Voucher
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {currentCoupon && (
          <div className="alert alert-info mb-3">
            <i className="bx bx-info-circle me-2"></i>
            Current voucher: <strong>{currentCoupon.code}</strong> (Discount: ₹{currentCoupon.discount?.toFixed(2)})
            <br />
            <small className="text-muted">
              <i className="bx bx-gift me-1"></i>
              Gift Voucher payment of ₹{currentCoupon.discount?.toFixed(2)} has been added automatically
            </small>
          </div>
        )}

        {errors.api && (
          <Alert variant="danger" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            {errors.api}
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="text-secondary">
              Discount Voucher <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              autoFocus
              placeholder="Enter discount voucher code"
              value={voucherCode}
              onChange={(e) => {
                setVoucherCode(e.target.value);
                clearError();
              }}
              isInvalid={!!errors.voucherCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.voucherCode}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Enter the discount voucher code to apply the discount.
              <br />
              <small><i className="bx bx-info-circle me-1"></i>A Gift Voucher payment will be automatically added to your payment list.</small>
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button className='add-btn' onClick={handleClose}>
          Cancel
        </Button>
        {currentCoupon && (
          <Button variant="warning" onClick={handleRemove}>
            <i className="bx bx-trash me-1"></i>
            Remove
          </Button>
        )}
        <Button className='submit-btn' onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
              Validating...
            </>
          ) : (
            <>
              {/* <i className="bx bx-check me-1"></i> */}
              {currentCoupon ? 'Update' : 'Apply'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};